import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, ModalProps, notification } from 'antd';
import { useEffect, useState, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, LoadingButton } from 'src/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from 'src/components/ui/form';
import { Input } from 'src/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { useSigner } from 'src/contexts/SignerContext';
import { useErc20Balance } from 'src/services/blockchain/get-erc20-balance';

import { MINIMUM_WITHDRAWAL, OTHER_TOKEN_MINIMUM_WITHDRAWAL } from 'src/constants/exchange';
import { useAuth } from 'src/libs/rainbow';
import { useSupportedTokens } from 'src/services/token/get-supported-tokens';
import { useGetUserPortfolio } from 'src/services/user/get-user-portfolio';
import { useRequestWithdrawTrade } from 'src/services/user/request-withdraw-trade';
import {
  getWithdrawalAmountTrade,
  useGetWithdrawalAmountTrade,
} from 'src/services/user/get-withdrawal-amount-trade';
import { useGetWithdrawalRevokePreview } from 'src/services/user/get-withdrawal-revoke-preview';
import { useAccount, useChainId } from 'wagmi';
import { z } from 'zod';
import Withdrawable from './Withdrawable';
import ListFundsDropdown from './dropdown-fund-withdraw-trade';
import { useAppSelector } from 'src/store';
import { useAppTranslation } from 'src/i18n/hooks';
import '../styles/withdraw-modal.scss';
import i18n from 'src/i18n';
import { roundDownToDecimals } from 'src/helpers/formatNumber';
import { AlertTriangle, CreditCard, Send, Trash } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from 'src/components/ui/card';
import { THEME_MODE } from 'src/interfaces/theme';
import TokenLogo from 'src/components/ui/TokenLogo';
import Web3ActionButton from 'src/components/02.buttons/Web3ActionButton';
import AmountInput from 'src/components/25.inputs/AmountInput';
import { useCancelWithdrawalTrade } from 'src/services/user/canel-withdrawal-trade';
import { CountdownState } from 'src/interfaces/common';
import moment from 'moment';
import { getCountdown } from 'src/helpers/date';
import { useGetUserInfo } from 'src/services/user/get-user-info';

export interface TransformedDataBalanceAllChain {
  chain_id: number;
  balance: number;
  token_name: string;
}

const withdrawFormSchema = z.object({
  token: z.string(),
  withdrawAmount: z
    .string()
    .refine((val) => val === '' || (!isNaN(parseFloat(val)) && parseFloat(val) > 0), {
      message: i18n.t('trade:positiveAmountNumber'),
    }),
});

type WithdrawFormValues = z.infer<typeof withdrawFormSchema>;

type WithdrawModalProps = Pick<ModalProps, 'open' | 'onClose'> & {
  onWithdrawSettle: () => void;
};

type WithdrawReqData = {
  tokenID: string;
  balance: number;
  chainID: number;
  publicNonce: {
    kPublic: string;
    kTwoPublic: string;
  };
  publicKey: string;
};

const WithdrawModal: React.FC<WithdrawModalProps> = ({ onClose, open, onWithdrawSettle }) => {
  const { t } = useAppTranslation('trade');
  const userTheme = useAppSelector((state) => state.theme);
  const chainId = useChainId();
  const { address } = useAccount();

  const [isLoading, setIsLoading] = useState(false);
  const [active, setActive] = useState('requestWithdraw');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const supportedTokensQueryForWithdraw = useSupportedTokens(1, false);
  const initialSelectedToken = supportedTokensQueryForWithdraw.data?.at?.(0)?.address ?? '';
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedChain, setSelectedChain] = useState<number>();
  const [loadingCancel, setLoadingCancel] = useState(false);

  const { data: userData } = useGetUserInfo(localStorage.getItem('userId') ?? '');
  const isTestAccount = userData?.role === 'PAPER_ACCOUNT';

  const {
    data: balanceAllChainQuery,
    isLoading: isLoadingBalanceAllChain,
    refetch: refetchBalance,
  } = useGetUserPortfolio();

  const [openDropdownFunds, setOpenDropdownFunds] = useState(false);
  const { isAuthenticated, isEthereumWallet } = useAuth();

  useEffect(() => {
    if (isAuthenticated && isEthereumWallet) {
      refetchBalance();
    }
  }, [isAuthenticated, openDropdownFunds, open, active, isEthereumWallet]);

  const form = useForm<WithdrawFormValues>({
    resolver: zodResolver(withdrawFormSchema),
    defaultValues: {
      token: initialSelectedToken,
      withdrawAmount: '',
    },
  });

  useEffect(() => {
    if (initialSelectedToken) {
      form.setValue('token', initialSelectedToken);
    }
  }, [initialSelectedToken]);

  const selectedTokenAddress = form.watch('token') ?? initialSelectedToken;
  const withdrawAmount = form.watch('withdrawAmount');

  const { getPublicNonces, getPublicKey } = useSigner();
  const [isWithdrawalDisabled, setIsWithdrawalDisabled] = useState(false);
  const erc20BalanceQuery = useErc20Balance({
    tokenAddress: selectedTokenAddress as `0x${string}`,
  });

  const [tokenTicker, setTokenTicker] = useState('USDT');
  const { mutateAsync: requestWithdrawTradeMutation, isPending: isLoadingRequestWithdrawTrade } =
    useRequestWithdrawTrade();

  const { data: withdrawableData, refetch: refetchWithdrawableData } = useGetWithdrawalAmountTrade({
    tokenID: tokenTicker,
    chainID: chainId,
  });
  const cancelWithdrawalMutation = useCancelWithdrawalTrade();

  const [countdown, setCountdown] = useState<CountdownState>({
    days: '00',
    hours: '00',
    minutes: '00',
    seconds: '00',
  });

  const [validateWithdrawalValue, setValidateWithdrawalValue] = useState(true);
  const [hasPendingWithdrawals, setHasPendingWithdrawals] = useState(false);

  useEffect(() => {
    if (supportedTokensQueryForWithdraw?.data) {
      supportedTokensQueryForWithdraw.data.find(({ address, token_id }) => {
        if (selectedTokenAddress === address) {
          setTokenTicker(token_id);
        }
      });
    }
  }, [selectedTokenAddress, supportedTokensQueryForWithdraw]);

  const withdrawValue = form.watch('withdrawAmount');
  const withdrawAmountValue = useMemo(() => {
    const parsed = parseFloat(withdrawValue);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [withdrawValue]);

  const transformedData = Object.entries(balanceAllChainQuery || {}).flatMap(([chainId, items]) =>
    items?.map((item) => ({
      ...item,
      chain_id: Number(chainId),
    })),
  );

  const { data: revokePreview } = useGetWithdrawalRevokePreview(
    { tokenId: tokenTicker, amount: withdrawAmountValue },
    { enabled: withdrawAmountValue > 0 && tokenTicker !== '' },
  );

  const rawMaxWithdrawSize =
    transformedData.find((item) => item?.token_name === tokenTicker && item.chain_id === chainId)
      ?.balance ?? 0;

  // Get token decimal info for proper rounding
  const tokenDecimal = useMemo(() => {
    const token = supportedTokensQueryForWithdraw.data?.find(
      ({ token_id }) => token_id === tokenTicker
    );
    return token?.decimal ?? 18;
  }, [supportedTokensQueryForWithdraw.data, tokenTicker]);

  // Round down to token's decimal places to ensure we never exceed actual balance
  const maxWithdrawSize = useMemo(() => {
    return roundDownToDecimals(rawMaxWithdrawSize, tokenDecimal);
  }, [rawMaxWithdrawSize, tokenDecimal]);

  const refetchErc20Balance = erc20BalanceQuery.refetch;

  useEffect(() => {
    const validateWithdrawal = () => {
      if (!validateWithdrawalValue) return;
      const balance = maxWithdrawSize;
      const amount = withdrawAmount === '' ? 0 : parseFloat(withdrawAmount);
      const minAmount = MINIMUM_WITHDRAWAL.get(tokenTicker) ?? OTHER_TOKEN_MINIMUM_WITHDRAWAL;

      if (balance < minAmount) {
        setIsWithdrawalDisabled(true);
        setErrorMessage(
          t('withdrawModal.insufficientBalance', { minAmount, tokenTicker })
        );
      } else if (withdrawAmount === '') {
        setIsWithdrawalDisabled(true);
        setErrorMessage(t('withdrawModal.pleaseEnterAmount'));
      } else if (amount < minAmount) {
        setIsWithdrawalDisabled(true);
        setErrorMessage(t('withdrawModal.minimumWithdrawal', { minAmount, tokenTicker }));
      } else if (amount > balance) {
        setIsWithdrawalDisabled(true);
        setErrorMessage(t('withdrawModal.exceededBalance'));
      } else {
        setIsWithdrawalDisabled(false);
        setErrorMessage(null);
      }
    };

    validateWithdrawal();
  }, [maxWithdrawSize, withdrawAmount, tokenTicker, validateWithdrawalValue, t]);

  useEffect(() => {
    setErrorMessage(null);
  }, []);

  useEffect(() => {
    setErrorMessage(null);
    form.setValue('withdrawAmount', '');
    if (withdrawAmount !== '') {
      setIsWithdrawalDisabled(false);
    }
  }, [tokenTicker]);

  // Check for pending withdrawals when token or chain changes
  useEffect(() => {
    const checkPendingWithdrawals = async () => {
      if (!tokenTicker || !chainId || !isAuthenticated) {
        setHasPendingWithdrawals(false);
        return;
      }

      try {
        const response = await getWithdrawalAmountTrade({
          tokenID: tokenTicker,
          chainID: chainId,
        });

        // Check if there are pending withdrawals
        const hasPending = response.pendingWithdrawals && response.pendingWithdrawals.length > 0;
        setHasPendingWithdrawals(hasPending);
      } catch (error) {
        console.error('Error checking pending withdrawals:', error);
        setHasPendingWithdrawals(false);
      }
    };

    if (!isEthereumWallet) return;
    checkPendingWithdrawals();
  }, [tokenTicker, chainId, isAuthenticated, isEthereumWallet]);

  // Function to refresh pending withdrawals status
  const refreshPendingWithdrawals = async () => {
    if (!tokenTicker || !chainId || !isAuthenticated) {
      setHasPendingWithdrawals(false);
      return;
    }

    try {
      const response = await getWithdrawalAmountTrade({
        tokenID: tokenTicker,
        chainID: chainId,
      });

      // Check if there are pending withdrawals
      const hasPending = response.pendingWithdrawals && response.pendingWithdrawals.length > 0;
      setHasPendingWithdrawals(hasPending);
    } catch (error) {
      console.error('Error checking pending withdrawals:', error);
      setHasPendingWithdrawals(false);
    }
  };

  // Refresh when switching views or modal opens/closes
  useEffect(() => {
    if (open && isEthereumWallet) {
      refreshPendingWithdrawals();
    }
  }, [open, active, isEthereumWallet]);

  let activeNotification: string | null = null;

  const handleRequestWithdrawConfirm = async () => {
    if(isTestAccount) {
      showErrorNotification(t('withdrawModal.withdrawalError'), t('withdrawModal.testAccountError'));
      return;
    }

    if (isWithdrawalDisabled || withdrawAmount === '') {
      const errorDesc = withdrawAmount === '' ? t('withdrawModal.pleaseWriteAmount') : errorMessage;
      showErrorNotification(t('withdrawModal.withdrawalError'), errorDesc ?? '');
      return;
    }
    setValidateWithdrawalValue(true);
    setValidateWithdrawalValue(false);
    setIsLoading(true);

    const withdrawReqData: WithdrawReqData = {
      tokenID: tokenTicker,
      balance: parseFloat(withdrawValue),
      chainID: chainId,
      publicKey: getPublicKey()?.toHex()!,
      publicNonce: {
        kPublic: getPublicNonces()?.kPublic.toHex()!,
        kTwoPublic: getPublicNonces()?.kTwoPublic.toHex()!,
      },
    };

    try {
      await requestWithdrawTradeMutation(withdrawReqData, {
        onError: handleWithdrawError,
        onSuccess: () => {
          setValidateWithdrawalValue(false);

          // Start transition animation
          setIsTransitioning(true);

          // Show success notification
          notification.success({
            message: t('withdrawModal.withdrawalRequestSent'),
            description: t('withdrawModal.withdrawalRequestSentDesc'),
            duration: 3,
          });

          // Reset form after delay
          setTimeout(() => {
            form.resetField('withdrawAmount');
          }, 1000);

          refetchErc20Balance();
          refetchWithdrawableData();
          // Auto-advance to next step after 2 seconds
          setTimeout(() => {
            setIsTransitioning(false);
            // setActive('withdrawable'); // temporary disable
            setHasPendingWithdrawals(true);
            setIsLoading(false);
          }, 2000);
        },
      });
    } catch (error) {
      handleWithdrawError(error);
      setIsTransitioning(false);
      setIsLoading(false);
    }
  };

  const handleCancelWithdraw = async () => {
    if (withdrawableData && withdrawableData?.pendingWithdrawals?.length > 0) {
      setLoadingCancel(true);
      await cancelWithdrawalMutation
        .mutateAsync(withdrawableData.pendingWithdrawals[0].id)
        .then(() => {
          setTimeout(() => {
            notification.success({
              message: t('withdrawModal.cancelWithdrawalSuccessful'),
              description: t('withdrawModal.cancelWithdrawalSuccessfulDesc'),
              placement: 'bottomRight',
              style: { fontFamily: 'Poppins' },
            });
            refetchWithdrawableData();
            setLoadingCancel(false);
            setHasPendingWithdrawals(false);
          }, 1000);
        })
        .catch((error) => {
          showErrorNotification(
            t('withdrawModal.cancelWithdrawalError'),
            t('withdrawModal.cancelWithdrawalErrorDesc'),
          );
        })
        .finally(() => {
          setLoadingCancel(false);
        });
    }
  };

  useEffect(() => {
    return () => {
      setValidateWithdrawalValue(true);
      setIsLoading(false);
      setIsTransitioning(false);
    };
  }, [open]);

  const handleWithdrawError = (error: any) => {
    console.error('Withdraw error:', error);
    setIsLoading(false);
    setIsTransitioning(false);

    let errorMessage = t('withdrawModal.cannotComplete');
    if (error?.response?.data?.code === 4092) {
      errorMessage = t('withdrawModal.pendingWithdrawal');
    } else if (error?.response?.status === 409) {
      errorMessage = t('withdrawModal.tryAgainLater');
    } else if (error?.response?.data?.message?.includes('open or pending positions')) {
      errorMessage = t('withdrawModal.openPositionsError');
    } else if (
      error?.response?.status === 500 &&
      error?.response?.data?.message?.includes('Failed to release lock')
    ) {
      errorMessage = t('withdrawModal.processingIssue');
    }

    showErrorNotification(t('withdrawModal.withdrawalError'), errorMessage);
  };

  const showNotification = (
    type: 'success' | 'error' | 'info',
    message: string,
    description: string,
  ) => {
    if (activeNotification) {
      notification.destroy(activeNotification);
    }

    activeNotification = `${type}-${Date.now()}`;
    notification[type]({
      message,
      description,
      placement: 'bottomRight',
      key: activeNotification,
      onClose: () => {
        activeNotification = null;
      },
    });
  };

  const showErrorNotification = (message: string, description: string) => {
    showNotification('error', message, description);
  };

  useEffect(() => {
    return () => {
      setActive('requestWithdraw');
      setIsTransitioning(false);
    };
  }, [open]);

  useEffect(() => {
    if (address) {
      setSelectedChain(chainId);
    }
  }, [address]);

  const handleEndCountdown = () => {
    setCountdown({
      days: '00',
      hours: '00',
      minutes: '00',
      seconds: '00',
    });
  };

  const updateCountDown = () => {
    const now = moment.utc();
    if (
      withdrawableData &&
      withdrawableData?.pendingWithdrawals?.length &&
      withdrawableData.pendingWithdrawals.length > 0
    ) {
      const targetDate = moment.utc(withdrawableData.pendingWithdrawals[0].availableAt);
      const diff = targetDate.diff(now);
      if (diff > 0) {
        setCountdown(getCountdown(diff));
        return true;
      }
      handleEndCountdown();
      return false;
    }
    handleEndCountdown();
    return false;
  };

  useEffect(() => {
    const hasPending =
      withdrawableData &&
      withdrawableData?.pendingWithdrawals?.length &&
      withdrawableData.pendingWithdrawals.length > 0;

    if (!hasPending) {
      handleEndCountdown();
      return;
    }

    updateCountDown();
    const interval = setInterval(() => {
      updateCountDown();
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [withdrawableData?.pendingWithdrawals?.[0]?.availableAt]);

  return (
    <Modal
      title={null}
      destroyOnClose
      open={open}
      onCancel={onClose}
      footer={null}
      className={`font-poppins z-10 withdraw-modal withdraw-modal--${userTheme}`}
      width={500}
    >
      <div className={`p-3 pt-8 sm:pt-6 ${userTheme === THEME_MODE.DARK ? 'dark' : ''}`}>
        {/* Header */}
        <div className="mb-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-black dark:text-white">
              {t('withdrawModal.title')}
            </h2>
            {/* View Pending / Back Button */}
            {/* <Button
              onClick={() => {
                if (active === 'requestWithdraw') setActive('withdrawable');
                else setActive('requestWithdraw');
                setIsTransitioning(false);
                // Refresh pending withdrawals when switching views
                refreshPendingWithdrawals();
              }}
              variant="outline"
              size="sm"
              className="bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-400/30 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-700 dark:hover:text-blue-300 hover:border-blue-300 dark:hover:border-blue-300/50 transition-all duration-200 px-2 py-1 text-xs min-w-[100px] h-7"
            >
              {active === 'requestWithdraw' ? (
                <>
                  <Clock className="w-3 h-3 mr-1" />
                  <span className="whitespace-nowrap">{t('withdrawModal.pendingWithdrawals')}</span>
                </>
              ) : (
                <>
                  <ArrowRight className="w-3 h-3 mr-1 rotate-180" />
                  <span>{t('withdrawModal.back')}</span>
                </>
              )}
            </Button> */}
          </div>
        </div>

        {active === 'requestWithdraw' && (
          <Card className={`shadow-lg bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 border ${userTheme === THEME_MODE.DARK ? 'border-gray-700 dark:border-gray-700' : 'border-gray-200'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base text-black dark:text-white">
                <CreditCard className="w-4 h-4 text-blue-500" />
                {t('withdrawModal.requestWithdrawalTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(handleRequestWithdrawConfirm)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="token"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-black dark:text-white">{t('withdrawModal.asset')}</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="h-10 bg-white dark:bg-gray-800 border hover:border-blue-300 transition-colors">
                              <SelectValue placeholder={t('withdrawModal.selectToken')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent
                            className={`z-[1000] asset-dropdown ${userTheme === THEME_MODE.DARK ? 'asset-dropdown-dark' : ''}`}
                            position="popper"
                            sideOffset={5}
                          >
                            {supportedTokensQueryForWithdraw.data?.map(({ address, token_id }) => (
                              <SelectItem key={address} value={address} className="h-10">
                                <div className="flex items-center gap-2">
                                  <TokenLogo tokenId={token_id} size="sm" />
                                  <span className={`font-medium ${userTheme === THEME_MODE.DARK ? 'text-white' : 'text-black'}`}>{token_id}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div>
                    <FormLabel className="text-sm font-medium text-black dark:text-white">{t('chain')}</FormLabel>
                    <ListFundsDropdown
                      chainId={selectedChain}
                      listSupportAsset={
                        supportedTokensQueryForWithdraw.data?.map(({ token_id }) => token_id) ?? []
                      }
                      customClassName="mt-1"
                      isLoadingBalanceAllChain={isLoadingBalanceAllChain}
                      balanceAllChainQuery={balanceAllChainQuery}
                      transformedDataBalanceAllChain={transformedData}
                      onSelectChain={setSelectedChain}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="withdrawAmount"
                    render={({ field: { onChange, value } }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-black dark:text-white">{t('withdrawModal.withdrawAmount')}</FormLabel>

                        <FormControl>
                          <AmountInput value={value} onChange={(e) => {
                            setValidateWithdrawalValue(true);
                            const value = e.target.value;
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              onChange(value);
                            }
                          }}
                            onMaxClick={() => {
                              onChange(maxWithdrawSize.toString());
                            }}

                            availableAmount={maxWithdrawSize.toString()} tokenTicker={tokenTicker} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Error Message */}
                  {errorMessage && (
                    <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <p className="text-red-600 dark:text-red-400 text-sm">{errorMessage}</p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="flex flex-col gap-2 pt-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400 text-center">
                      {t('withdrawModal.withdrawNote', {
                        countdown: hasPendingWithdrawals
                          ? `${countdown.days}d ${countdown.hours}h ${countdown.minutes}m ${countdown.seconds}s`
                          : '24h',
                      })}
                    </span>

                    {/* Min Balance for Reward Warning */}
                    {(() => {
                      const currentTokenData = transformedData.find(
                        (item) => item?.token_name === tokenTicker && item.chain_id === chainId,
                      );
                      const minBalanceForReward = currentTokenData?.min_balance_for_reward ?? 0;

                      if (revokePreview?.will_revoke && minBalanceForReward > 0) {
                        return (
                          <div className="text-xs text-red-500 dark:text-red-400 text-center font-bold">
                            {t('withdrawModal.minBalanceForRewardWarning', {
                              minBalance: minBalanceForReward.toLocaleString(),
                            })}
                          </div>
                        );
                      }
                      return null;
                    })()}

                    <Web3ActionButton selectedNetwork={selectedChain}>
                      <LoadingButton
                        type="submit"
                        className="w-full h-10 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 bg-[linear-gradient(90deg,#0920CF_0%,#5415DE_50%,#CB01F7_100%)] hover:opacity-90 disabled:opacity-50"
                        loading={isLoading || isLoadingRequestWithdrawTrade}
                        disabled={hasPendingWithdrawals || isWithdrawalDisabled}
                        // onClick={(e) => {
                        //   if (hasPendingWithdrawals) {
                        //     e.preventDefault();
                        //     setActive('withdrawable');
                        //     setIsTransitioning(false);
                        //   }
                        // }}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {hasPendingWithdrawals
                          ? t('withdrawModal.processingWithdrawal')
                          : t('withdrawModal.confirmWithdrawal')}
                      </LoadingButton>
                    </Web3ActionButton>
                    {hasPendingWithdrawals && (
                      <Web3ActionButton selectedNetwork={selectedChain}>
                        <LoadingButton
                          type="button"
                          className="w-full h-10 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 bg-[#D83232] hover:opacity-90 disabled:opacity-50"
                          onClick={handleCancelWithdraw}
                          loading={loadingCancel}
                        >
                          <Trash className="w-4 h-4 mr-2" />
                          {t('withdrawModal.cancelWithdraw', {
                            amount: Number(
                              withdrawableData?.pendingWithdrawals?.[0].amount ?? '0',
                            ).toLocaleString(),
                            tokenTicker: withdrawableData?.pendingWithdrawals?.[0].tokenID,
                          })}
                        </LoadingButton>
                      </Web3ActionButton>
                    )}
                  </div>
                </form>
              </FormProvider>
            </CardContent>
          </Card>
        )}

        {active === 'withdrawable' && (
          <Card
            className={`shadow-lg bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 border ${userTheme === THEME_MODE.DARK ? 'border-gray-700 dark:border-gray-700' : 'border-gray-200'}`}
          >
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base text-black dark:text-white">
                <Send className="w-4 h-4 text-green-500" />
                {t('withdrawModal.completeWithdrawal')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              <Web3ActionButton selectedNetwork={selectedChain}>
                <Withdrawable
                  chainId={chainId}
                  supportedTokensQuery={supportedTokensQueryForWithdraw}
                  onWithdrawSettle={onWithdrawSettle}
                  selectedToken={selectedTokenAddress}
                />
              </Web3ActionButton>
            </CardContent>
          </Card>
        )}
      </div>
    </Modal>
  );
};
export default WithdrawModal;
